<template>
  <!-- Present before any outcome lands, so screen readers announce what is rendered into it. -->
  <div class="slot-inspector-form-status" role="status">
    <sp-alert v-if="errorCopy" class="slot-inspector-form-status__error" type="error" icon>
      <p class="slot-inspector-form-status__error-title">{{ errorCopy.title }}</p>
      <p class="slot-inspector-form-status__error-body">{{ errorCopy.body }}</p>
      <p v-if="errorCopy.detail" class="slot-inspector-form-status__error-detail">{{ errorCopy.detail }}</p>
    </sp-alert>

    <div v-else-if="empty" class="slot-inspector-form-status__empty">
      <p class="slot-inspector-form-status__empty-title">{{ t("no_results_title") }}</p>
      <p class="slot-inspector-form-status__empty-body">{{ t("no_results_body") }}</p>
    </div>

    <SlotInspectorEhrWarning v-if="warning" :warning="warning" />
  </div>
</template>

<script setup>
import { computed } from "vue";
import { PreviewErrorCode } from "#ce/apps/SpAdmin/SpSlotInspectorApp/api/slot-inspector";
import { t } from "#ce/apps/SpAdmin/SpSlotInspectorApp/i18n";
import { STATUS, useSlotInspectorStore } from "#ce/apps/SpAdmin/SpSlotInspectorApp/store/slot-inspector";
import SlotInspectorEhrWarning from "./SlotInspectorEhrWarning.ce.vue";

const KNOWN_ERROR_CODES = new Set(Object.values(PreviewErrorCode));

const { status, error, report } = useSlotInspectorStore();

const empty = computed(() => status.value === STATUS.EMPTY);

// An error code the app does not know reads as a failed request. Only an EHR failure shows the server's message,
// which carries the EHR's own reason for the Sparkle team.
const errorCopy = computed(() => {
  if (status.value !== STATUS.ERROR) {
    return null;
  }

  const code = KNOWN_ERROR_CODES.has(error.value?.code) ? error.value.code : PreviewErrorCode.REQUEST_FAILED;

  return {
    title: t(`error_${code}_title`),
    body: t(`error_${code}_body`),
    detail: code === PreviewErrorCode.EHR_REQUEST_FAILED ? error.value.message : "",
  };
});

const warning = computed(() => {
  if (status.value === STATUS.ERROR) {
    return error.value?.warning ?? null;
  }

  if (status.value === STATUS.EMPTY) {
    return report.value?.notInEhrWarning ?? null;
  }

  return null;
});
</script>

<style scoped lang="scss">
.slot-inspector-form-status {
  display: grid;
  gap: var(--sp-ref-scale-16);

  // The region stays rendered while it is empty, so the space above it comes with its first message.
  > :first-child {
    margin-block-start: var(--sp-ref-scale-24);
  }

  &__error {
    --sp-comp-alert-error-background-color: var(--sp-sys-color-error-container);
    --sp-comp-alert-error-text-color: var(--sp-sys-color-on-surface);
    --sp-comp-alert-border: 1px solid color-mix(in srgb, var(--sp-sys-color-error) 45%, var(--sp-sys-color-surface));
    --sp-comp-alert-padding: var(--sp-ref-scale-16);
    --sp-comp-alert-prepend-icon-size: var(--sp-ref-scale-20);
  }

  &__error-title,
  &__error-body,
  &__error-detail,
  &__empty-title,
  &__empty-body {
    margin: 0;
    font-size: var(--sp-sys-typescale-small-font-size);
  }

  &__error-title {
    margin-block-end: var(--sp-ref-scale-4);
    font-weight: var(--sp-sys-font-weight-medium);
    color: color-mix(in srgb, var(--sp-sys-color-error) 60%, var(--sp-ref-palette-black));
  }

  &__error-detail {
    margin-block-start: var(--sp-ref-scale-8);
    color: var(--sp-sys-color-neutral-secondary);
    overflow-wrap: anywhere;
  }

  &__empty {
    display: grid;
    justify-items: center;
    gap: var(--sp-ref-scale-4);
    padding: var(--sp-ref-scale-32) var(--sp-ref-scale-16);
    border-radius: var(--sp-ref-border-radius-sm);
    background-color: var(--sp-sys-color-surface-variant);
    text-align: center;
  }

  &__empty-title {
    font-weight: var(--sp-sys-font-weight-medium);
    color: var(--sp-sys-color-on-surface);
  }

  &__empty-body {
    color: var(--sp-sys-color-neutral-secondary);
  }
}
</style>
